import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { CustomButton, CustomTable, Text } from '../../shared/uiComponents';
import { Container } from '../../components/StyledComponents';
import { PageHeader } from '../../components/PageHeader';
import { useCheckPermission } from '../../shared/Helpers/hooks';
import { errorNormalizer } from '../../shared/Helpers/functions';
import { PERMISSIONS } from '../../App/constants';
import { Settings } from '../../shared/Assets/menuIcons';
import { Headers, TableBody, PlannedHeaders, PlannedTableBody } from './tableInfo';

import { DispatchProperties, useSelector } from '../../redux/store';
import {
	dryRunMonday,
	getMondaySyncLogs,
	getMondaySyncStatus,
	syncMondayNow,
} from '../../redux/State/clientSlice/mondaySyncSlice';
import { MondaySyncDryRunResultProperties } from '../../redux/API/ClientAPIHelpers/mondaySync';

const formatDryRunSummary = (r: MondaySyncDryRunResultProperties) =>
	`would create ${r.created}, update ${r.updated}, skip ${r.skipped} of ${r.totalFetched} fetched`;

const MondaySync = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const { permissionGranted } = useCheckPermission(PERMISSIONS.SUPERADMIN);

	const logs = useSelector((state) => state.mondaySync.logs);
	const loading = useSelector((state) => state.mondaySync.loading);
	const syncing = useSelector((state) => state.mondaySync.syncing);
	const dryRunning = useSelector((state) => state.mondaySync.dryRunning);
	const dryRunResult = useSelector((state) => state.mondaySync.dryRunResult);
	const status = useSelector((state) => state.mondaySync.status);

	const prevIsRunningRef = useRef(false);

	useEffect(() => {
		if (!permissionGranted) return;
		dispatch(getMondaySyncStatus());
		dispatch(getMondaySyncLogs(100));
	}, [dispatch, permissionGranted]);

	// While a run is in progress, poll status; when it finishes, refresh the log once.
	useEffect(() => {
		const isRunning = status?.isRunning ?? false;
		const wasRunning = prevIsRunningRef.current;
		prevIsRunningRef.current = isRunning;

		if (isRunning) {
			const id = setInterval(() => dispatch(getMondaySyncStatus()), 10000);
			return () => clearInterval(id);
		}
		if (wasRunning && !isRunning) {
			dispatch(getMondaySyncLogs(100));
		}
	}, [status?.isRunning, dispatch]);

	if (!permissionGranted) return null;

	const handleDryRun = () => {
		dispatch(dryRunMonday())
			.then(unwrapResult)
			.then((result) => {
				toast(`Dry run: ${formatDryRunSummary(result)}. No changes made.`);
			})
			.catch(errorNormalizer);
	};

	const handleSyncNow = () => {
		dispatch(syncMondayNow())
			.then(unwrapResult)
			.then(() => {
				toast('Monday sync started in the background.');
				dispatch(getMondaySyncStatus()); // pick up the Running status → starts polling
			})
			.catch(errorNormalizer);
	};

	const renderStatus = () => {
		if (!status) return 'No sync has run yet.';
		if (status.isRunning) {
			return `Sync in progress (started ${dayjs(status.startedAt).format('MM/DD/YYYY hh:mm A')})…`;
		}
		const when = status.completedAt ? dayjs(status.completedAt).format('MM/DD/YYYY hh:mm A') : '—';
		const base =
			`Last run (${status.trigger}) ${status.status} at ${when}: ` +
			`created ${status.created}, updated ${status.updated}, skipped ${status.skipped}, failed ${status.failed} ` +
			`of ${status.totalFetched}.`;
		return status.errorMessage ? `${base} Error: ${status.errorMessage}` : base;
	};

	return (
		<Container>
			<PageHeader
				icon={<Settings />}
				title="Monday Sync"
				isInContainer
				customStyles={{ marginBottom: '24px' }}
				headerContent={
					<div style={{ display: 'flex', gap: '12px' }}>
						<CustomButton title="Dry Run" onClick={handleDryRun} loading={dryRunning} buttonType="secondary" />
						<CustomButton
							title="Sync Now"
							onClick={handleSyncNow}
							loading={syncing || status?.isRunning}
						/>
					</div>
				}
			/>

			{dryRunResult && (
				<div style={{ marginBottom: '24px' }}>
					<Text
						type="psm"
						fontWeight="semiBold"
						title={`Dry run preview — no changes saved: ${formatDryRunSummary(dryRunResult)}.`}
					/>
					<div style={{ marginTop: '12px' }}>
						<CustomTable
							headers={PlannedHeaders}
							data={{ query: dryRunResult.planned }}
							TableBody={PlannedTableBody}
							loading={dryRunning}
							hasPagination={false}
						/>
					</div>
				</div>
			)}

			<div style={{ marginBottom: '16px' }}>
				<Text type="psm" fontWeight="semiBold" title="Saved sync history — manual and hourly runs" />
				<div style={{ marginTop: '8px' }}>
					<Text type="psm" title={renderStatus()} />
				</div>
			</div>

			<CustomTable
				headers={Headers}
				data={{ query: logs }}
				TableBody={TableBody}
				loading={loading}
				hasPagination={false}
			/>
		</Container>
	);
};

export default MondaySync;
